import React, { useRef, useCallback, useEffect } from 'react';
import { Eraser, Check } from 'lucide-react';

interface SignaturePadProps {
  /** Called with base64 PNG data URL when user confirms signature */
  onSave: (dataUrl: string) => void;
  /** Optional: existing signature to display (base64 data URL) */
  value?: string;
  /** Optional: called when signature is cleared */
  onClear?: () => void;
  /** Optional: label text */
  label?: string;
  /**
   * `app` (default) is the dark in-app card. `document` sits inside an
   * existing white card on the public ticket review page: no card of its own,
   * and gray-on-white text, which the in-app text colours are invisible on.
   */
  tone?: 'app' | 'document';
}

const TONES = {
  app: {
    wrapper: 'bg-fieldiq-card rounded-xl p-4',
    title: 'font-semibold mb-3 text-white',
    reSign: 'flex items-center gap-2 text-sm text-fieldiq-text hover:text-white transition-colors',
    hint: 'text-xs text-fieldiq-muted mb-3',
    clear: 'flex items-center gap-1.5 px-3 py-2 bg-fieldiq-border text-fieldiq-text rounded-lg text-sm hover:text-white transition-colors',
    confirmOn: 'bg-fieldiq-success text-white hover:bg-fieldiq-success/90',
    confirmOff: 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed',
    savedFrame: 'bg-white rounded-lg p-2 mb-3',
    canvasFrame: 'bg-white rounded-lg overflow-hidden border-2 border-fieldiq-border mb-3',
  },
  document: {
    wrapper: '',
    title: 'block text-xs text-gray-500 mb-1',
    reSign: 'flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors',
    hint: 'text-xs text-gray-500 mb-3',
    clear: 'flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm',
    confirmOn: 'bg-green-600 text-white hover:bg-green-700',
    confirmOff: 'bg-gray-100 text-gray-400 cursor-not-allowed',
    savedFrame: 'bg-white border rounded-lg p-2 mb-3',
    canvasFrame: 'bg-white rounded-lg overflow-hidden border-2 border-gray-300 mb-3',
  },
} as const;

export default function SignaturePad({ onSave, value, onClear, label = 'Signature', tone = 'app' }: SignaturePadProps) {
  const t = TONES[tone];
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const [hasSigned, setHasSigned] = React.useState(!!value);
  const [savedUrl, setSavedUrl] = React.useState<string | null>(value || null);

  // Fill white background on mount
  useEffect(() => {
    if (savedUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth;
    canvas.height = 160;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [savedUrl]);

  const getPos = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      const touch = e.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    isDrawing.current = true;
    lastPos.current = getPos(e);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing.current || !lastPos.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    if (!hasSigned) setHasSigned(true);
  };

  const endDraw = () => {
    isDrawing.current = false;
    lastPos.current = null;
  };

  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    setHasSigned(false);
    setSavedUrl(null);
    onClear?.();
  }, [onClear]);

  const handleConfirm = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSigned) return;
    const dataUrl = canvas.toDataURL('image/png');
    setSavedUrl(dataUrl);
    onSave(dataUrl);
  }, [onSave, hasSigned]);

  return (
    <div className={t.wrapper}>
      <h3 className={t.title}>{label}</h3>

      {savedUrl ? (
        <div>
          <div className={t.savedFrame}>
            <img src={savedUrl} alt="Signature" className="max-h-[120px] mx-auto" />
          </div>
          <button
            type="button"
            onClick={handleClear}
            className={t.reSign}
          >
            <Eraser size={16} /> Re-sign
          </button>
        </div>
      ) : (
        <div>
          <div className={t.canvasFrame} style={{ touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              className="w-full"
              style={{ width: '100%', height: 160, display: 'block' }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
              onTouchCancel={endDraw}
            />
          </div>
          <p className={t.hint}>Draw your signature above</p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClear}
              className={t.clear}
            >
              <Eraser size={14} /> Clear
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!hasSigned}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasSigned ? t.confirmOn : t.confirmOff
              }`}
            >
              <Check size={14} /> Confirm Signature
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
